import { config, origin } from './config';
import { faqEntries, t, type Lang } from './i18n';
import { PATHS, pathFor, type Route } from './routes';

const OG_IMAGE: Record<Route, string> = {
  home: '/og.jpg',
  waste: '/og-waste.jpg',
  vision: '/og-vision.jpg',
  demo: '/og.jpg',
  privacy: '/og.jpg',
  terms: '/og.jpg',
};

/** The largest image above the fold on each route, preloaded from the static head. */
export const LCP_IMAGE: Record<Route, { href: string; srcset?: string; sizes?: string }> = {
  home: {
    href: '/images/waste/iris-waste-map-laptop-frameless.webp',
    srcset:
      '/images/waste/iris-waste-map-laptop-frameless.webp 1440w, /images/waste/iris-waste-map-laptop-frameless@2x.webp 2880w',
    sizes: '(min-width: 1024px) 45vw, 100vw',
  },
  waste: {
    href: '/images/waste/iris-waste-map-laptop-frameless.webp',
    srcset:
      '/images/waste/iris-waste-map-laptop-frameless.webp 1440w, /images/waste/iris-waste-map-laptop-frameless@2x.webp 2880w',
    sizes: '(min-width: 1024px) 50vw, 100vw',
  },
  vision: {
    href: '/images/vision/iris-vision-alerts-laptop-frameless.webp',
    srcset:
      '/images/vision/iris-vision-alerts-laptop-frameless.webp 1440w, /images/vision/iris-vision-alerts-laptop-frameless@2x.webp 2880w',
    sizes: '(min-width: 1024px) 50vw, 100vw',
  },
  demo: { href: '' },
  privacy: { href: '' },
  terms: { href: '' },
};

export interface PageMeta {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogImageAlt: string;
  url: string;
  alternates: { hreflang: string; href: string }[];
  jsonld: object;
}

export function pageUrl(route: Route, lang: Lang = 'en'): string {
  return `${origin()}${pathFor(route, lang)}`;
}

function jsonLd(route: Route, lang: Lang) {
  const base = origin();
  const url = pageUrl(route, lang);
  const org = {
    '@type': 'Organization',
    '@id': `${base}/#org`,
    name: 'IRIS',
    url: `${base}/`,
    logo: `${base}/favicon.svg`,
    brand: { '@type': 'Brand', name: 'IRIS' },
    email: config.salesEmail,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '125 Banafseg',
      addressLocality: 'New Cairo',
      addressCountry: 'EG',
    },
  };
  const site = {
    '@type': 'WebSite',
    '@id': `${base}/#site`,
    url: `${base}/`,
    name: 'IRIS',
    inLanguage: ['en', 'ar'],
    publisher: { '@id': `${base}/#org` },
  };
  const page = (type: string) => ({
    '@type': type,
    '@id': `${url}#page`,
    url,
    name: t(lang, `meta.${route}.title`),
    description: t(lang, `meta.${route}.description`),
    inLanguage: lang,
    isPartOf: { '@id': `${base}/#site` },
  });
  const crumbs = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'IRIS', item: `${base}/` },
      { '@type': 'ListItem', position: 2, name: t(lang, `meta.${route}.title`).split(' | ')[0], item: url },
    ],
  };
  const product = (line: 'waste' | 'vision') => ({
    '@type': 'Product',
    '@id': `${base}${PATHS[line]}#product`,
    name: t(lang, `line.${line}`),
    description: t(lang, `jsonld.${line}`),
    brand: { '@type': 'Brand', name: 'IRIS' },
    manufacturer: { '@id': `${base}/#org` },
    url: `${base}${PATHS[line]}`,
    offers: {
      '@type': 'Offer',
      name: t(lang, `jsonld.offer.name.${line}`),
      description: t(lang, `jsonld.${line}.offer`),
      url: `${base}${PATHS.demo}?line=${line}`,
    },
  });
  const faq = (prefix: string) => ({
    '@type': 'FAQPage',
    '@id': `${url}#faq`,
    inLanguage: lang,
    mainEntity: faqEntries(lang, prefix).map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  });

  const graph: object[] = [org, site];
  if (route === 'home') {
    graph.push({ ...page('WebPage'), about: [{ '@id': `${base}/waste#product` }, { '@id': `${base}/vision#product` }] });
    graph.push(product('waste'), product('vision'));
  } else if (route === 'waste' || route === 'vision') {
    graph.push({ ...page('WebPage'), mainEntity: { '@id': `${base}${PATHS[route]}#product` } }, product(route), crumbs, faq(route === 'waste' ? 'wfaq' : 'vfaq'));
  } else if (route === 'demo') {
    graph.push(page('ContactPage'), crumbs, faq('faq'));
  } else {
    graph.push(page('WebPage'), crumbs);
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

export function pageMeta(route: Route, lang: Lang): PageMeta {
  const url = pageUrl(route, lang);
  return {
    title: t(lang, `meta.${route}.title`),
    description: t(lang, `meta.${route}.description`),
    ogTitle: t(lang, `og.${route}.title`),
    ogDescription: t(lang, `og.${route}.description`),
    ogImage: `${origin()}${OG_IMAGE[route]}`,
    ogImageAlt: t(lang, `og.${route}.imageAlt`),
    url,
    alternates: [
      { hreflang: 'en', href: pageUrl(route, 'en') },
      { hreflang: 'ar', href: pageUrl(route, 'ar') },
      { hreflang: 'x-default', href: pageUrl(route, 'en') },
    ],
    jsonld: jsonLd(route, lang),
  };
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Static head for the prerendered page of a route and language. */
export function headHtml(route: Route, lang: Lang = 'en'): string {
  const m = pageMeta(route, lang);
  const lcp = LCP_IMAGE[route];
  const lines = [
    `<title>${esc(m.title)}</title>`,
    `<meta name="description" content="${esc(m.description)}" />`,
    `<link rel="canonical" href="${esc(m.url)}" />`,
    ...m.alternates.map((a) => `<link rel="alternate" hreflang="${a.hreflang}" href="${esc(a.href)}" />`),
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="IRIS" />`,
    `<meta property="og:title" content="${esc(m.ogTitle)}" />`,
    `<meta property="og:description" content="${esc(m.ogDescription)}" />`,
    `<meta property="og:url" content="${esc(m.url)}" />`,
    `<meta property="og:image" content="${esc(m.ogImage)}" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="${esc(m.ogImageAlt)}" />`,
    `<meta property="og:locale" content="${lang === 'ar' ? 'ar' : 'en_GB'}" />`,
    `<meta property="og:locale:alternate" content="${lang === 'ar' ? 'en_GB' : 'ar'}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${esc(m.ogTitle)}" />`,
    `<meta name="twitter:description" content="${esc(m.ogDescription)}" />`,
    `<meta name="twitter:image" content="${esc(m.ogImage)}" />`,
  ];
  if (lcp.href) {
    const set = lcp.srcset ? ` imagesrcset="${lcp.srcset}" imagesizes="${lcp.sizes}"` : '';
    lines.push(`<link rel="preload" as="image" href="${lcp.href}"${set} fetchpriority="high" />`);
  }
  lines.push(
    `<script type="application/ld+json" id="iris-jsonld">${JSON.stringify(m.jsonld).replace(/</g, '\\u003c')}</script>`,
  );
  return lines.join('\n    ');
}
